import movementAnalyticsEndpoints from './endpoints/movementAnalytics.endpoints';

/**
 * Get real-time movement summary for a person.
 * @param {number} personId - The person/alien identifier.
 * @param {number} [hours=24] - Hours of history to analyze.
 * @returns {Promise<Object|null>} Movement summary data.
 */
export async function getMovementSummary(personId, hours = 24) {
  try {
    const response = await movementAnalyticsEndpoints.getSummary({ params: { person_id: personId, hours } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[getMovementSummary] Error:', error);
    return null;
  }
}

/**
 * Check if velocity exceeds threshold.
 * @param {number} personId - The person/alien identifier.
 * @param {number} [thresholdMph=85] - Speed threshold in mph.
 * @returns {Promise<Object|null>} Alert data if threshold exceeded.
 */
export async function checkVelocityThreshold(personId, thresholdMph = 85) {
  try {
    const response = await movementAnalyticsEndpoints.checkVelocity({ params: { person_id: personId, threshold_mph: thresholdMph } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[checkVelocityThreshold] Error:', error);
    return null;
  }
}

/**
 * Get movement state display configuration.
 * @param {string} state - The movement state.
 * @returns {Object} Configuration with icon, color, bgColor, and label.
 */
export function getMovementStateConfig(state) {
  const configs = {
    stationary: { icon: '\u23F8', color: '#6B7280', bgColor: '#F3F4F6', label: 'Stationary' },
    walking: { icon: '\uD83D\uDEB6', color: '#22C55E', bgColor: '#DCFCE7', label: 'Walking' },
    running: { icon: '\uD83C\uDFC3', color: '#F59E0B', bgColor: '#FEF3C7', label: 'Running' },
    driving: { icon: '\uD83D\uDE97', color: '#3B82F6', bgColor: '#DBEAFE', label: 'Driving' },
    driving_fast: { icon: '\uD83D\uDE98', color: '#F97316', bgColor: '#FED7AA', label: 'Fast Driving' },
    suspicious: { icon: '\u26A0', color: '#EF4444', bgColor: '#FEE2E2', label: 'Suspicious Speed' },
    impossible: { icon: '\u274C', color: '#991B1B', bgColor: '#FCA5A5', label: 'Impossible (Spoof?)' },
    unknown: { icon: '\u2753', color: '#6B7280', bgColor: '#F3F4F6', label: 'Unknown' },
  };
  return configs[state] || configs.unknown;
}

/**
 * Get heading direction display.
 * @param {string} direction - Cardinal direction (N, NE, E, etc.)
 * @returns {Object} Configuration with arrow and label.
 */
export function getHeadingConfig(direction) {
  const configs = {
    N: { arrow: '\u2191', label: 'North' },
    NE: { arrow: '\u2197', label: 'Northeast' },
    E: { arrow: '\u2192', label: 'East' },
    SE: { arrow: '\u2198', label: 'Southeast' },
    S: { arrow: '\u2193', label: 'South' },
    SW: { arrow: '\u2199', label: 'Southwest' },
    W: { arrow: '\u2190', label: 'West' },
    NW: { arrow: '\u2196', label: 'Northwest' },
  };
  return configs[direction] || configs.N;
}

/**
 * Format speed for display.
 * @param {number} speedMph - Speed in mph.
 * @returns {string} Formatted speed string.
 */
export function formatSpeed(speedMph) {
  if (speedMph === null || speedMph === undefined) return 'N/A';
  if (speedMph < 1) return '< 1 mph';
  return `${speedMph.toFixed(1)} mph`;
}

/**
 * Format distance for display.
 * @param {number} miles - Distance in miles.
 * @returns {string} Formatted distance string.
 */
export function formatDistance(miles) {
  if (miles === null || miles === undefined) return 'N/A';
  if (miles < 0.1) return `${Math.round(miles * 5280)} ft`;
  return `${miles.toFixed(2)} mi`;
}

/**
 * Format duration for display.
 * @param {number} minutes - Duration in minutes.
 * @returns {string} Formatted duration string.
 */
export function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '0 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Get speed color based on value.
 * @param {number} speedMph - Speed in mph.
 * @returns {string} Color hex code.
 */
export function getSpeedColor(speedMph) {
  if (speedMph < 5) return '#6B7280';     // Gray - stationary/walking
  if (speedMph < 25) return '#22C55E';    // Green - slow
  if (speedMph < 65) return '#3B82F6';    // Blue - normal
  if (speedMph < 85) return '#F59E0B';    // Amber - fast
  if (speedMph < 120) return '#EF4444';   // Red - very fast
  return '#991B1B';                        // Dark red - suspicious
}
